import { ArrowUpBigIcon } from '@blocksuite/affine-components/icons';
import { stopPropagation } from '@blocksuite/affine-shared/utils';
import type { EditorHost } from '@blocksuite/block-std';
import { WithDisposable } from '@blocksuite/block-std';
import type { AIPanelProTip } from '@pique/core/modules/onboarding/ai-panel-pro-tips';
import { AI_PANEL_PRO_TIPS } from '@pique/core/modules/onboarding/ai-panel-pro-tips';
import { baseTheme } from '@toeverything/theme';
import { css, html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { AIStarIcon, AIStarIconWithAnimation } from '../_common/icons';

export const ShareIcon = html`<svg
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.54-7.54l-1.154 1.154"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  ></path>
  <path
    d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.54 7.54l1.154-1.154"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  ></path>
</svg>`;

export const BulbIcon = html`<svg
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    fill="currentColor"
    d="M16.79,17.217 C16.677,18.137 16.283,19.249 15.225,19.818 C15.137,20.629 14.936,21.355 14.462,21.889 C13.866,22.56 13.003,22.761 12.008,22.751 C10.922,22.769 10.052,22.514 9.473,21.811 C9.017,21.257 8.835,20.526 8.745,19.767 C8.302,19.487 7.965,19.159 7.722,18.764 C7.422,18.277 7.305,17.751 7.244,17.24 C7.152,16.476 7.768,15.867 8.476,15.867 L15.571,15.867 C16.246,15.867 16.885,16.438 16.79,17.217 Z M12,6.25 C8.846,6.25 6.25,8.894 6.25,12.206 C6.25,13.097 6.438,13.939 6.775,14.695 C6.943,15.074 6.773,15.517 6.394,15.685 C6.016,15.854 5.572,15.683 5.404,15.305 C4.984,14.36 4.75,13.31 4.75,12.206 C4.75,8.11 7.974,4.75 12,4.75 C16.026,4.75 19.25,8.11 19.25,12.206 C19.25,13.31 19.016,14.36 18.596,15.305 C18.427,15.683 17.984,15.854 17.606,15.685 C17.227,15.517 17.057,15.074 17.225,14.695 C17.562,13.939 17.75,13.097 17.75,12.206 C17.75,8.894 15.154,6.25 12,6.25 Z M8.999,17.978 C9.114,18.164 9.299,18.358 9.643,18.557 L14.384,18.557 C14.838,18.37 15.114,17.956 15.245,17.367 L8.78,17.367 C8.83,17.623 8.899,17.816 8.999,17.978 Z M10.631,20.858 C10.801,21.064 11.129,21.267 11.992,21.251 L11.992,21.251 C12,21.251 12.007,21.251 12.015,21.251 C12.842,21.261 13.169,21.087 13.339,20.894 C13.476,20.74 13.594,20.488 13.675,20.057 L10.309,20.057 C10.388,20.442 10.495,20.692 10.631,20.858 Z M4.399,4.399 C4.692,4.106 5.167,4.106 5.46,4.399 L6.167,5.106 C6.46,5.399 6.46,5.874 6.167,6.167 C5.874,6.46 5.399,6.46 5.106,6.167 L4.399,5.46 C4.106,5.167 4.106,4.692 4.399,4.399 Z M12,1.25 C12.414,1.25 12.75,1.586 12.75,2 L12.75,3 C12.75,3.414 12.414,3.75 12,3.75 C11.586,3.75 11.25,3.414 11.25,3 L11.25,2 C11.25,1.586 11.586,1.25 12,1.25 Z M1.25,12 C1.25,11.586 1.586,11.25 2,11.25 L3,11.25 C3.414,11.25 3.75,11.586 3.75,12 C3.75,12.414 3.414,12.75 3,12.75 L2,12.75 C1.586,12.75 1.25,12.414 1.25,12 Z M21,11.25 L22,11.25 C22.414,11.25 22.75,11.586 22.75,12 C22.75,12.414 22.414,12.75 22,12.75 L21,12.75 C20.586,12.75 20.25,12.414 20.25,12 C20.25,11.586 20.586,11.25 21,11.25 Z M19.601,5.46 L18.894,6.167 C18.601,6.46 18.126,6.46 17.833,6.167 C17.54,5.874 17.54,5.399 17.833,5.106 L18.54,4.399 C18.833,4.106 19.308,4.106 19.601,4.399 C19.894,4.692 19.894,5.167 19.601,5.46 Z"
  />
</svg>`;

export const AIChatIcon = html`<svg
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <g clip-path="url(#clip0_ai_chat)">
    <path
      d="M11.2812 5.49104C11.2403 5.13024 10.9353 4.85751 10.5722 4.85714C10.2091 4.85677 9.90345 5.12887 9.86185 5.48959C9.59131 7.83515 8.89003 9.48448 7.75868 10.6158C6.62734 11.7472 4.97801 12.4485 2.63244 12.719C2.27173 12.7606 1.99963 13.0662 2 13.4293C2.00037 13.7924 2.2731 14.0975 2.63389 14.1383C4.94069 14.3996 6.62508 15.1006 7.78328 16.2379C8.93713 17.3709 9.65305 19.0198 9.85994 21.3489C9.89271 21.7178 10.2019 22.0004 10.5722 22C10.9425 21.9996 11.2511 21.7162 11.2831 21.3473C11.4813 19.0565 12.1966 17.3729 13.3562 16.2133C14.5157 15.0537 16.1994 14.3385 18.4902 14.1402C18.8591 14.1083 19.1424 13.7997 19.1429 13.4294C19.1433 13.0591 18.8606 12.7499 18.4918 12.7171C16.1627 12.5102 14.5137 11.7943 13.3807 10.6404C12.2435 9.48222 11.5425 7.79783 11.2812 5.49104Z"
      fill="currentColor"
    />
    <path
      d="M18.9427 2.24651C18.9268 2.1062 18.8082 2.00014 18.667 2C18.5257 1.99986 18.4069 2.10567 18.3907 2.24595C18.2855 3.15811 18.0128 3.79952 17.5728 4.23949C17.1329 4.67946 16.4914 4.95218 15.5793 5.05739C15.439 5.07356 15.3332 5.19241 15.3333 5.33362C15.3335 5.47482 15.4395 5.59345 15.5798 5.60935C16.4769 5.71096 17.132 5.98357 17.5824 6.42584C18.0311 6.86644 18.3095 7.50771 18.39 8.41347C18.4027 8.55691 18.523 8.66683 18.667 8.66667C18.811 8.6665 18.931 8.55632 18.9434 8.41284C19.0205 7.52199 19.2987 6.86723 19.7496 6.41629C20.2006 5.96534 20.8553 5.68719 21.7462 5.61008C21.8896 5.59766 21.9998 5.47765 22 5.33365C22.0002 5.18964 21.8902 5.06939 21.7468 5.05664C20.841 4.97619 20.1998 4.69777 19.7592 4.24905C19.3169 3.79864 19.0443 3.1436 18.9427 2.24651Z"
      fill="currentColor"
    />
  </g>
  <defs>
    <clipPath id="clip0_ai_chat">
      <rect width="24" height="24" fill="white" />
    </clipPath>
  </defs>
</svg>`;

export const FeedbackIcon = html`<svg
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M8 13.5H16M8 8.5H12" stroke="currentColor" />
  <path
    d="M6.09881 19C4.7987 18.8721 3.82475 18.4816 3.17157 17.8284C2 16.6569 2 14.7712 2 11V10.5C2 6.72876 2 4.84315 3.17157 3.67157C4.34315 2.5 6.22876 2.5 10 2.5H14C17.7712 2.5 19.6569 2.5 20.8284 3.67157C22 4.84315 22 6.72876 22 10.5V11C22 14.7712 22 16.6569 20.8284 17.8284C19.6569 19 17.7712 19 14 19C13.4395 19.0125 12.9931 19.0551 12.5546 19.155C11.3562 19.4309 10.2465 20.0441 9.14987 20.5789C7.58729 21.3408 6.806 21.7218 6.31569 21.3651C5.37769 20.6665 6.29454 18.5019 6.5 17.5"
    stroke="currentColor"
  />
</svg>`;

export const OpenItemIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 24 24"
  width="14"
  height="14"
  fill="none"
>
  <g transform="rotate(45 12 12)">
    <g transform="translate(0 -1)">
      <path
        fill="currentColor"
        stroke="currentColor"
        stroke-width="0.3"
        d="M17.434,10.49 C15.2,7.965 12.66,5.751 11.999,5.751 C11.337,5.751 8.799,7.965 6.565,10.49 C6.289,10.8 5.817,10.828 5.506,10.555 C5.196,10.281 5.166,9.808 5.441,9.496 C5.486,9.444 6.581,8.211 7.889,6.955 C10.388,4.558 11.397,4.25 11.999,4.25 C12.6,4.25 13.608,4.557 16.108,6.955 C17.416,8.211 18.511,9.444 18.556,9.496 C18.83,9.806 18.801,10.28 18.492,10.555 C18.349,10.681 18.172,10.742 17.997,10.743 L17.995,10.743 C17.788,10.742 17.583,10.657 17.434,10.49 Z"
      />
    </g>
    <g transform="translate(0 1)">
      <path
        fill="currentColor"
        stroke="currentColor"
        stroke-width="0.3"
        d="M7.892,17.036 C6.599,15.792 5.517,14.569 5.446,14.489 L5.444,14.487 C5.17,14.177 5.2,13.704 5.51,13.43 C5.821,13.156 6.294,13.186 6.569,13.496 C8.802,16.028 11.34,18.249 12.001,18.249 C12.663,18.249 15.203,16.027 17.436,13.496 C17.709,13.185 18.183,13.156 18.494,13.43 C18.804,13.704 18.834,14.177 18.56,14.487 L18.558,14.489 C18.488,14.569 17.405,15.792 16.112,17.036 C13.612,19.44 12.603,19.748 12.001,19.748 C11.401,19.748 10.391,19.44 7.892,17.036 Z"
      />
    </g>
  </g>
</svg>`;

// Types we need
export interface AIResponse {
  id: string;
  content: string;
  type?: string;
}

export interface AIAction {
  id: string;
  label: string;
  handler: () => void;
}

export interface AIPanelAnswerConfig {
  responses: AIResponse[];
  actions: AIAction[];
}

export interface AIPanelErrorConfig {
  error?: {
    type?: string;
    message?: string;
  };
  responses: AIResponse[];
  cancel: () => void;
  login: () => void;
  upgrade: () => void;
}

export interface AIPanelGeneratingConfig {
  generatingIcon?: unknown;
  stages?: Array<{ name: string; duration?: number }>;
  height?: number;
}

export interface CopyConfig {
  allowed: boolean;
  onCopy: () => string;
}

// AI Panel Input Component
@customElement('pique-ai-panel-input')
export class PiqueAIPanelInput extends WithDisposable(LitElement) {
  private readonly _onInput = () => {
    this._textarea.style.height = 'auto';
    this._textarea.style.height = this._textarea.scrollHeight + 'px';

    this.onInput?.(this._textarea.value);
    const value = this._textarea.value.trim();
    if (value.length > 0) {
      this._arrow.dataset['active'] = '';
      this._hasContent = true;
    } else {
      delete this._arrow.dataset['active'];
      this._hasContent = false;
    }
  };

  private readonly _onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      this._sendToAI();
    }
  };

  private readonly _sendToAI = () => {
    const value = this._textarea.value.trim();
    if (value.length === 0) return;

    this.onFinish?.(value);
    this.remove();
  };

  static override styles = css`
    :host {
      width: 100%;
      padding: 0 12px;
      box-sizing: border-box;
    }

    .root {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      background: var(--affine-background-overlay-panel-color);
    }

    .icon {
      display: flex;
      align-items: center;
    }

    .textarea-container {
      display: flex;
      align-items: flex-end;
      gap: 8px;
      flex: 1 0 0;

      textarea {
        flex: 1 0 0;
        border: none;
        outline: none;
        -webkit-box-shadow: none;
        -moz-box-shadow: none;
        box-shadow: none;
        background-color: transparent;
        resize: none;
        overflow: hidden;
        padding: 0px;

        color: var(--affine-text-primary-color);

        /* light/sm */
        font-family: var(--affine-font-family);
        font-size: var(--affine-font-sm);
        font-style: normal;
        font-weight: 400;
        line-height: 22px; /* 157.143% */
      }

      textarea::placeholder {
        color: var(--affine-placeholder-color);
      }

      textarea::-moz-placeholder {
        color: var(--affine-placeholder-color);
      }
    }

    .arrow {
      display: flex;
      align-items: center;
      padding: 2px;
      gap: 10px;
      border-radius: 4px;
      background: var(--affine-black-10, rgba(0, 0, 0, 0.1));

      svg {
        width: 16px;
        height: 16px;
        color: var(--affine-pure-white, #fff);
      }
    }
    .arrow[data-active] {
      background: var(--affine-brand-color, #1e96eb);
    }
    .arrow[data-active]:hover {
      cursor: pointer;
    }
  `;

  override render() {
    this.updateComplete
      .then(() => {
        requestAnimationFrame(() => {
          this._textarea.focus();
        });
      })
      .catch(console.error);

    return html`<div class="root">
      <div class="icon">${AIStarIcon}</div>
      <div class="textarea-container">
        <textarea
          placeholder="Ask AI to edit or generate..."
          rows="1"
          @keydown=${this._onKeyDown}
          @input=${this._onInput}
          @pointerdown=${stopPropagation}
          @click=${stopPropagation}
          @dblclick=${stopPropagation}
          @cut=${stopPropagation}
          @copy=${stopPropagation}
          @paste=${stopPropagation}
          @keyup=${stopPropagation}
        ></textarea>
        <div
          class="arrow"
          @click=${this._sendToAI}
          @pointerdown=${stopPropagation}
        >
          ${ArrowUpBigIcon}
        </div>
      </div>
    </div>`;
  }

  override updated(_changedProperties: Map<PropertyKey, unknown>): void {
    const result = super.updated(_changedProperties);
    this._textarea.style.height = this._textarea.scrollHeight + 'px';
    return result;
  }

  @query('.arrow')
  private accessor _arrow!: HTMLDivElement;

  @state()
  private accessor _hasContent = false;

  @query('textarea')
  private accessor _textarea!: HTMLTextAreaElement;

  @property({ attribute: false })
  accessor onFinish: ((input: string) => void) | undefined = undefined;

  @property({ attribute: false })
  accessor onInput: ((input: string) => void) | undefined = undefined;

  @property({ attribute: false })
  accessor onBlur: (() => void) | undefined = undefined;
}

// AI Panel Answer Component - Simplified since we auto-insert
@customElement('pique-ai-panel-answer')
export class PiqueAIPanelAnswer extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      display: none; /* Hidden since we auto-insert */
    }
  `;

  override render() {
    return nothing; // No UI needed since we auto-insert
  }

  @property({ attribute: false })
  accessor config!: AIPanelAnswerConfig;

  @property({ attribute: false })
  accessor copy: CopyConfig | undefined = undefined;

  @property({ attribute: false })
  accessor finish = true;

  @property({ attribute: false })
  accessor host!: EditorHost;
}

// AI Panel Generating Component
@customElement('pique-ai-panel-generating')
export class PiqueAIPanelGenerating extends WithDisposable(LitElement) {
  private _tipIndex = -1;
  private _intervalId: ReturnType<typeof setInterval> | null = null;
  private _timeoutIds: ReturnType<typeof setTimeout>[] = [];
  private _shuffledTips: AIPanelProTip[] = [];
  private _showProTips = false;
  private _isTransitioning = false;

  private _shuffleTips() {
    const tips = [...AI_PANEL_PRO_TIPS];
    this._shuffledTips = tips.sort(() => Math.random() - 0.5);
  }

  private _startCarousel() {
    if (this._intervalId) return;

    this._shuffleTips();
    this._showProTips = false;
    this._isTransitioning = false;
    this._tipIndex = -1;

    const outerTimeout = setTimeout(() => {
      this._isTransitioning = true;
      this.requestUpdate();

      const innerTimeout = setTimeout(() => {
        this._isTransitioning = false;
        this._showProTips = true;
        this._tipIndex = 0;
        this.requestUpdate();

        this._intervalId = setInterval(() => {
          this._isTransitioning = true;
          this.requestUpdate();

          const transitionTimeout = setTimeout(() => {
            this._isTransitioning = false;
            this._tipIndex = (this._tipIndex + 1) % this._shuffledTips.length;
            this.requestUpdate();
          }, 300);
          this._timeoutIds.push(transitionTimeout);
        }, 6000);
      }, 300);
      this._timeoutIds.push(innerTimeout);
    }, 1600);
    this._timeoutIds.push(outerTimeout);
  }

  private _stopCarousel() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
    for (const id of this._timeoutIds) {
      clearTimeout(id);
    }
    this._timeoutIds = [];
  }

  updateLoadingProgress(_progress?: number) {
    // Optional loading progress method for compatibility
  }

  private _renderTipContent(content: string) {
    const placeholderRegex =
      /({{shareButton}}|{{aiChatButton}}|{{commandZ}}|{{feedbackButton}}|{{openIcon}})/g;
    const parts = content.split(placeholderRegex);
    return parts.map(part => {
      switch (part) {
        case '{{shareButton}}':
          return html`<span class="inline-icon-button">${ShareIcon} Share</span>`;
        case '{{aiChatButton}}':
          return html`<span class="inline-ai-chat-icon">${AIChatIcon}</span>`;
        case '{{commandZ}}':
          return html`<span class="inline-kbd"><kbd>Cmd</kbd> + <kbd>Z</kbd></span>`;
        case '{{feedbackButton}}':
          return html`<span class="inline-icon-button">${FeedbackIcon}</span>`;
        case '{{openIcon}}':
          return html`<span class="inline-open-icon">${OpenItemIcon}</span>`;
        default:
          return unsafeHTML(part);
      }
    });
  }

  static override styles = css`
    :host {
      width: 100%;
      padding: 0 12px;
      box-sizing: border-box;
      font-family: ${unsafeCSS(baseTheme.fontSansFamily)};
    }

    .generating-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }

    .icon {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .icon > * {
      width: 28px;
      height: 28px;
    }

    .text-area {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-height: 36px;
    }

    .text-content {
      font-size: var(--affine-font-sm);
      line-height: 1.5;
      text-align: center;
      color: var(--affine-text-primary-color);
      opacity: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .text-content.fade-in {
      animation: textFadeIn 0.5s cubic-bezier(0.25, 1, 0.5, 1);
    }

    .text-content.fade-out {
      animation: textFadeOut 0.5s cubic-bezier(0.25, 1, 0.5, 1);
      opacity: 0;
    }

    .status {
      color: var(--affine-brand-color);
      font-weight: 500;
    }

    .text-content strong {
      font-weight: 600;
      color: var(--affine-text-primary-color);
    }

    .pro-tip-container {
      text-align: center;
    }

    .tip-label-container {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: var(--affine-brand-color);
      font-weight: 600;
      vertical-align: middle;
    }

    .tip-label-container svg {
      vertical-align: middle;
    }

    .tip-text {
      color: var(--affine-text-primary-color);
      display: inline;
      vertical-align: middle;
    }

    .inline-icon-button {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      color: var(--affine-brand-color);
      font-weight: 600;
      vertical-align: middle;
      transform: translateY(-1px);
    }

    .inline-icon-button svg {
      vertical-align: middle;
    }

    .inline-ai-chat-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 0.5px solid #8c5cf5;
      box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.05);
      background: var(--affine-background-overlay-panel-color);
      margin: 0 3px;
      vertical-align: middle;
      transform: translateY(-1px);
      color: var(--affine-icon-color);
    }

    .inline-ai-chat-icon svg {
      width: 12px;
      height: 12px;
    }

    .inline-kbd {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      vertical-align: middle;
    }

    .inline-kbd kbd {
      display: inline-block;
      padding: 2px 6px;
      font-size: 11px;
      font-family: var(--affine-font-family);
      font-weight: 500;
      line-height: 1.2;
      color: var(--affine-text-primary-color);
      background: var(--affine-background-secondary-color);
      border: 1px solid var(--affine-border-color);
      border-radius: 4px;
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
    }

    .inline-open-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 1px solid var(--pique-green-border);
      box-shadow: var(--pique-green-shadow-medium);
      margin: 0 3px;
      vertical-align: middle;
      transform: translateY(-1px);
      color: var(--affine-text-primary-color);
      background: linear-gradient(
        90deg,
        rgb(200, 240, 215) 0%,
        rgb(180, 230, 200) 100%
      );
    }

    .inline-open-icon svg {
      width: 10px;
      height: 10px;
    }

    :host-context(html[data-theme='dark']) .inline-open-icon {
      background: linear-gradient(
        90deg,
        rgb(46, 164, 93) 0%,
        rgb(36, 144, 78) 100%
      );
    }

    @keyframes textFadeIn {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes textFadeOut {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(-8px);
      }
    }

    .progress-wrapper {
      display: flex;
      justify-content: center;
      height: 0;
      opacity: 0;
      overflow: hidden;
      transition:
        height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.4s ease,
        margin-top 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      margin-top: 0;
    }

    .progress-wrapper.visible {
      height: 2px;
      opacity: 1;
      margin-top: 10px;
    }

    .tip-progress {
      width: 40px;
      height: 2px;
      background: var(--affine-border-color);
      border-radius: 2px;
      overflow: hidden;
      opacity: 0.4;
    }

    .tip-progress-bar {
      height: 100%;
      background: var(--affine-brand-color);
      border-radius: 2px;
      box-shadow: 0 0 6px var(--affine-brand-color);
      animation: progressBar 6s linear infinite;
    }

    @keyframes progressBar {
      from {
        width: 0%;
      }
      to {
        width: 100%;
      }
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    this._startCarousel();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._stopCarousel();
  }

  override render() {
    const { generatingIcon = AIStarIconWithAnimation } = this.config;
    const currentTip =
      this._tipIndex >= 0 ? this._shuffledTips[this._tipIndex] : null;

    const textClass = this._isTransitioning
      ? 'text-content fade-out'
      : 'text-content fade-in';
    const showProgress = this._showProTips && currentTip;

    return html`
      <div class="generating-container">
        <div class="icon">${generatingIcon}</div>
        <div class="text-area">
          <div class="${textClass}" .key=${this._tipIndex}>
            ${this._showProTips && currentTip
              ? html`
                  <div class="pro-tip-container">
                    <span class="tip-label-container">
                      ${BulbIcon} Pro tip:
                    </span>
                    ${' '}
                    <span class="tip-text">
                      ${this._renderTipContent(currentTip.content)}
                    </span>
                  </div>
                `
              : html` <div class="status">Pique at work...</div> `}
          </div>
          <div class="progress-wrapper ${showProgress ? 'visible' : ''}">
            <div class="tip-progress"><div class="tip-progress-bar"></div></div>
          </div>
        </div>
      </div>
    `;
  }

  @property({ attribute: false })
  accessor config!: AIPanelGeneratingConfig;

  @property({ attribute: false })
  accessor stopGenerating!: () => void;

  @property({ attribute: false })
  accessor withAnswer!: boolean;
}

// AI Panel Error Component - Simplified
@customElement('pique-ai-panel-error')
export class PiqueAIPanelError extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      width: 100%;
      padding: 12px;
      font-family: ${unsafeCSS(baseTheme.fontSansFamily)};
    }

    .error-info {
      color: var(--affine-error-color, #eb4335);
      font-size: var(--affine-font-sm);
      font-weight: 400;
      line-height: 22px;
    }
  `;

  override render() {
    const errorMessage = this.config.error?.message || 'AI request failed';
    return html`<div class="error-info">${errorMessage}</div>`;
  }

  @property({ attribute: false })
  accessor config!: AIPanelErrorConfig;

  @property({ attribute: false })
  accessor copy: CopyConfig | undefined = undefined;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor withAnswer = false;
}

// Global declarations
declare global {
  interface HTMLElementTagNameMap {
    'pique-ai-panel-input': PiqueAIPanelInput;
    'pique-ai-panel-answer': PiqueAIPanelAnswer;
    'pique-ai-panel-generating': PiqueAIPanelGenerating;
    'pique-ai-panel-error': PiqueAIPanelError;
  }
}
